import { useEffect, useState } from 'react'
import { Plus, Trash2, Link2 } from 'lucide-react'

import { adminMutate, apiGet, useToast } from '../admin/primitives.js'

/**
 * SALLES & DJs.
 *
 * Deux choses qui n'en font qu'une : une salle n'existe, opérationnellement,
 * que par ce qu'on y programme, et un DJ ne se compte que si ses différentes
 * orthographes désignent bien la même personne.
 *
 * Le point dur est l'annuaire. La programmation ne stockait qu'un nom libre :
 * « Madjack », « MADJACK » et « Mad Jack » comptaient pour trois artistes, et
 * personne ne pouvait le voir. Les noms NON RATTACHÉS sont donc mis en avant —
 * tant qu'ils traînent, les comptages sont faux en silence.
 */

interface Dj {
  id: string; name: string; aliases: string[]
  sets_count: number; last_played_at: string | null; rooms: string[]
}
interface Vue {
  djs: Dj[]
  unassigned: { name: string; occurrences: number }[]
  rooms: { code: string; label: string }[]
}

function quandFr(iso: string | null): string {
  if (!iso) return 'jamais programmé'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris',
  })
}

export function RoomsDjsPanel({ peutGerer }: { peutGerer: boolean }) {
  const toast = useToast()
  const [v, setV] = useState<Vue | null>(null)
  const [nouveau, setNouveau] = useState('')
  // Le nom non rattaché en cours d'attribution. Null = aucun.
  const [aRattacher, setARattacher] = useState<string | null>(null)

  const charger = () => {
    void apiGet<Vue>('/api/v1/soirees/djs').then(setV).catch(() => setV(null))
  }
  useEffect(charger, [])

  const creer = async (nom: string) => {
    const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>(
      'POST', '/api/v1/soirees/djs', { name: nom })
    if (r.status === 200) { setNouveau(''); charger() }
    else toast('error', r.data?.message ?? 'Création impossible.')
  }

  const rattacher = async (djId: string, alias: string) => {
    const r = await adminMutate<{ ok?: boolean; rattachees?: number; message?: string; error?: string }>(
      'POST', `/api/v1/soirees/djs/${djId}/aliases`, { alias })
    setARattacher(null)
    if (r.status === 200) {
      // On dit COMBIEN de lignes viennent de changer : sans ce nombre, on ne
      // sait pas si le rattachement a servi à quelque chose.
      const n = r.data?.rattachees ?? 0
      toast('ok', n > 0
        ? `${n} ligne${n > 1 ? 's' : ''} de programmation rattachée${n > 1 ? 's' : ''}.`
        : 'Alias ajouté — aucune ligne existante ne le portait.')
      charger()
    } else toast('error', r.data?.message ?? 'Rattachement impossible.')
  }

  const retirerAlias = async (djId: string, alias: string) => {
    await adminMutate('DELETE', `/api/v1/soirees/djs/${djId}/aliases/${encodeURIComponent(alias)}`)
    charger()
  }

  const supprimer = async (dj: Dj) => {
    await adminMutate('DELETE', `/api/v1/soirees/djs/${dj.id}`)
    toast('ok', `${dj.name} retiré de l’annuaire — ses passages restent lisibles.`)
    charger()
  }

  if (!v) return null

  /* §48-§54 — une salle sans programmation n'a rien à montrer. On liste ce qui
     a réellement lieu, et une seule action mène à la suite. */
  const sallesUtilisees = v.rooms.filter((r) => v.djs.some((d) => d.rooms.includes(r.code)))

  return (
    <>
      <section className="lc-panel gd-panel" aria-labelledby="set-salles-ref">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="set-salles-ref">Salles</h2>
        </header>
        <div className="lc-panel__body">
          {sallesUtilisees.length === 0 ? (
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Aucune salle n’a de programmation rattachée pour l’instant. Les salles
              du référentiel ({v.rooms.map((r) => r.label).join(' · ') || 'aucune'})
              apparaîtront ici dès qu’un DJ y sera rattaché.
            </p>
          ) : (
            <dl className="lc-kv">
              {sallesUtilisees.map((r) => {
                const djs = v.djs.filter((d) => d.rooms.includes(r.code))
                return (
                  <div key={r.code}>
                    <dt>{r.label}</dt>
                    <dd>{djs.length} artiste{djs.length > 1 ? 's' : ''} — {djs
                      .slice(0, 6).map((d) => d.name).join(' · ')}
                      {djs.length > 6 ? ` … +${djs.length - 6}` : ''}</dd>
                  </div>
                )
              })}
            </dl>
          )}
        </div>
      </section>

      {/* Les noms non reconnus d'abord : c'est là qu'est le travail. */}
      {v.unassigned.length > 0 ? (
        <section className="lc-panel gd-panel" aria-labelledby="set-djs-orphelins">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="set-djs-orphelins">Noms non rattachés</h2>
          </header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Ces noms ont été programmés mais ne désignent encore personne dans
              l’annuaire. Tant qu’ils y restent, les passages qu’ils représentent
              ne sont comptés pour aucun artiste.
            </p>
            <ul className="lc-list" style={{ marginTop: 12 }}>
              {v.unassigned.map((u) => (
                <li
                  key={u.name}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <strong style={{ color: 'var(--text-1)' }}>{u.name}</strong>
                  <span className="lc-hint">
                    {u.occurrences} passage{u.occurrences > 1 ? 's' : ''}
                  </span>
                  {peutGerer ? (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {aRattacher === u.name ? (
                        <select
                          className="lc-input" aria-label={`Rattacher ${u.name}`}
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) void rattacher(e.target.value, u.name)
                          }}
                        >
                          <option value="" disabled>Choisir un artiste…</option>
                          {v.djs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      ) : (
                        <>
                          <button
                            type="button" className="lc-btn"
                            onClick={() => setARattacher(u.name)}
                            disabled={v.djs.length === 0}
                          >
                            <Link2 size={14} aria-hidden="true" /> Rattacher
                          </button>
                          <button
                            type="button" className="lc-btn"
                            onClick={() => void creer(u.name)}
                          >
                            <Plus size={14} aria-hidden="true" /> Nouvel artiste
                          </button>
                        </>
                      )}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <section className="lc-panel gd-panel" aria-labelledby="set-djs">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="set-djs">Annuaire des DJs</h2>
        </header>
        <div className="lc-panel__body">
          {v.djs.length === 0 ? (
            <p className="lc-hint" style={{ marginTop: 0 }}>
              L’annuaire est vide. Les noms programmés continuent de s’afficher sur
              les soirées — ils ne sont simplement rattachés à personne.
            </p>
          ) : (
            <ul className="lc-list">
              {v.djs.map((d) => (
                <li
                  key={d.id}
                  style={{ padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                    <strong style={{ color: 'var(--text-1)' }}>{d.name}</strong>
                    <span className="lc-hint">
                      {d.sets_count} passage{d.sets_count > 1 ? 's' : ''} ·
                      {' '}dernier : {quandFr(d.last_played_at)}
                      {d.rooms.length > 0 ? ` · ${d.rooms.join(' · ')}` : ''}
                    </span>
                    {peutGerer ? (
                      <button
                        type="button" className="lc-iconbtn" style={{ marginLeft: 'auto' }}
                        aria-label={`Retirer ${d.name} de l’annuaire`}
                        onClick={() => void supprimer(d)}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                  {d.aliases.length > 0 ? (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {d.aliases.map((a) => (
                        <span key={a} className="lc-hint" style={{
                          border: '1px solid var(--border)', borderRadius: 'var(--radius-control)',
                          padding: '2px 8px',
                        }}>
                          {a}
                          {peutGerer ? (
                            <button
                              type="button" className="lc-iconbtn"
                              aria-label={`Retirer l’alias ${a}`}
                              style={{ marginLeft: 6 }}
                              onClick={() => void retirerAlias(d.id, a)}
                            >
                              <Trash2 size={12} aria-hidden="true" />
                            </button>
                          ) : null}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {/* Une seule porte d'entrée pour ajouter, comme demandé. */}
          {peutGerer ? (
            <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                className="lc-input" value={nouveau} placeholder="Nom de l’artiste"
                aria-label="Nom du nouvel artiste"
                onChange={(e) => setNouveau(e.target.value)}
              />
              <button
                type="button" className="lc-btn lc-btn--primary"
                disabled={nouveau.trim() === ''} onClick={() => void creer(nouveau)}
              >
                <Plus size={14} aria-hidden="true" /> Ajouter
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </>
  )
}
